/* assets/rod-pages.js : WP-10 — behaviour for the rod content pages (SPEC 5.18):
   - table of contents on the generic page section,
   - client-side FAQ filtering + deep links on the help page,
   - shared form validation (contact, password-page sign-up consent) with Turkish messages supplied
     by the markup (no UI copy lives in this file). SPEC 15.2 removed the corporate quote form, which
     was the only user of the `vkn` rule, so that rule was deleted with it,
   - contact-form URL prefill (?konu=, ?urun=, ?q=).
   Everything is scoped to [data-rod-page-root] so the footer newsletter (WP-8) is never double-bound. */

import { Rod } from '@rod/core';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TEL_RE = /^[\d\s()+\-./]+$/;

/* ------------------------------------------------------------------ helpers */

/** Turkish-aware fold with a safe fallback when @rod/core is unavailable. */
function fold(value) {
  const s = String(value == null ? '' : value);
  try {
    return Rod && typeof Rod.fold === 'function' ? Rod.fold(s) : s.toLocaleLowerCase('tr-TR');
  } catch (e) {
    return s.toLowerCase();
  }
}

function announce(text) {
  try {
    if (Rod && typeof Rod.announce === 'function') Rod.announce(text);
  } catch (e) {
    /* announcements are progressive enhancement */
  }
}

function debounce(fn, ms) {
  try {
    if (Rod && typeof Rod.debounce === 'function') return Rod.debounce(fn, ms);
  } catch (e) {
    /* fall through */
  }
  let t = null;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function slugify(text) {
  return fold(text)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function digitsOf(value) {
  return String(value == null ? '' : value).replace(/\D+/g, '');
}

/* ------------------------------------------------------------------ 1. table of contents */

function initToc(root) {
  const toc = root.querySelector('[data-rod-toc]');
  const source = root.querySelector('[data-rod-toc-source]');
  if (!toc || !source) return;
  const list = toc.querySelector('[data-rod-toc-list]');
  if (!list) return;

  const headings = Array.from(source.querySelectorAll('h2'));
  if (headings.length < 3) return;

  const used = new Set();
  const frag = document.createDocumentFragment();
  headings.forEach((heading, index) => {
    const label = (heading.textContent || '').trim();
    if (!label) return;
    let id = heading.id;
    if (!id) {
      const base = slugify(label) || 'bolum';
      id = base;
      let n = 2;
      while (used.has(id) || document.getElementById(id)) id = `${base}-${n++}`;
      heading.id = id;
    }
    used.add(id);
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${id}`;
    a.textContent = label;
    li.appendChild(a);
    frag.appendChild(li);
    void index;
  });
  if (!frag.childNodes.length) return;
  list.replaceChildren(frag);
  toc.hidden = false;
  // The 240px desktop column is reserved only now: without a TOC the prose keeps the full width.
  const layout = toc.closest('.rod-page__layout') || root.querySelector('[data-rod-toc-layout]');
  if (layout) layout.classList.add('is-toc');
  if (window.matchMedia('(min-width: 990px)').matches) toc.open = true;

  // The heading ids exist only from here on, so the browser's own hash scroll at load time found
  // nothing: a shared or bookmarked `/pages/hakkimizda#biz-kimiz` opened at the top of the page.
  // Re-apply the current hash once the ids are in place (and on later hash changes, which is a no-op
  // for in-page clicks because the browser already scrolls to the now-existing target).
  const goToHash = () => {
    let hash = '';
    try {
      hash = decodeURIComponent((location.hash || '').replace(/^#/, ''));
    } catch (e) {
      hash = (location.hash || '').replace(/^#/, '');
    }
    if (!hash) return;
    const target = document.getElementById(hash);
    if (!target || !source.contains(target)) return;
    requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'auto' }));
  };
  window.addEventListener('hashchange', goToHash);
  goToHash();
}

/* ------------------------------------------------------------------ 2. help page */

/** Rebuild an element's text with <mark> around every match of the folded needle. */
function paintHighlight(el, original, needle) {
  if (!needle) {
    el.textContent = original;
    return;
  }
  const haystack = fold(original);
  const parts = [];
  let from = 0;
  let at = haystack.indexOf(needle, from);
  while (at !== -1) {
    if (at > from) parts.push(document.createTextNode(original.slice(from, at)));
    const mark = document.createElement('mark');
    mark.className = 'rod-help__hl';
    mark.textContent = original.slice(at, at + needle.length);
    parts.push(mark);
    from = at + needle.length;
    at = haystack.indexOf(needle, from);
  }
  if (from < original.length) parts.push(document.createTextNode(original.slice(from)));
  el.replaceChildren(...parts);
}

function initHelp(root) {
  const list = root.querySelector('[data-rod-faq-list]');
  if (!list) return;
  const items = Array.from(list.querySelectorAll('[data-rod-faq-item]'));
  if (!items.length) return;

  const input = root.querySelector('[data-rod-help-search]');
  const empty = root.querySelector('[data-rod-help-empty]');
  const chips = Array.from(root.querySelectorAll('[data-rod-help-topic]'));
  const countTpl = (list.getAttribute('data-rod-count-template') || '').trim();

  const records = items.map((item) => {
    const label = item.querySelector('[data-rod-faq-label]');
    const original = label ? (label.textContent || '').trim() : '';
    return {
      item,
      label,
      original,
      topic: item.getAttribute('data-rod-faq-topic') || '',
      haystack: fold(`${original} ${(item.textContent || '').trim()}`),
    };
  });

  let topic = '';

  const apply = (announceCount) => {
    const raw = input ? input.value.trim() : '';
    const needle = raw ? fold(raw) : '';
    let shown = 0;
    records.forEach((rec) => {
      const okTopic = !topic || rec.topic === topic;
      const okText = !needle || rec.haystack.indexOf(needle) !== -1;
      const visible = okTopic && okText;
      rec.item.hidden = !visible;
      if (visible) shown += 1;
      if (rec.label) paintHighlight(rec.label, rec.original, needle);
    });
    if (empty) empty.hidden = shown !== 0;
    if (announceCount && countTpl) announce(countTpl.replace('{n}', String(shown)));
  };

  if (input) {
    input.addEventListener('input', debounce(() => apply(true), 200));
    input.addEventListener('search', () => apply(true));
    input.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      apply(true);
    });
  }

  chips.forEach((chip) => {
    chip.addEventListener('click', () => {
      const value = chip.getAttribute('data-rod-help-topic') || '';
      topic = topic === value ? '' : value;
      chips.forEach((c) => c.setAttribute('aria-pressed', (c.getAttribute('data-rod-help-topic') || '') === topic ? 'true' : 'false'));
      apply(true);
    });
  });

  const openFromHash = () => {
    const hash = decodeURIComponent((location.hash || '').replace(/^#/, ''));
    if (!hash) return;
    const target = items.find((item) => item.id === hash);
    if (!target) return;
    // A deep link shows the whole list again: keeping an active query or topic while forcing one
    // hidden item visible would contradict the visible count and the "Sonuç bulunamadı" row.
    if (input) input.value = '';
    if (topic) {
      topic = '';
      chips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
    }
    apply(false);
    target.open = true;
    requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'auto' }));
  };

  window.addEventListener('hashchange', openFromHash);
  openFromHash();
}

/* ------------------------------------------------------------------ 3. forms */

function messageFor(control) {
  const id = control.getAttribute('aria-describedby');
  if (!id) return null;
  for (const part of id.split(/\s+/)) {
    const el = document.getElementById(part);
    if (el && el.classList.contains('rod-form__msg')) return el;
  }
  return null;
}

function showError(control, text) {
  control.setAttribute('aria-invalid', 'true');
  const msg = messageFor(control);
  if (msg) {
    // The paragraph carries role="alert" in the markup; it is revealed before the text is written so
    // that every invalid field is announced, not only the one that receives focus.
    if (!msg.hasAttribute('role')) msg.setAttribute('role', 'alert');
    msg.hidden = false;
    msg.textContent = text;
  }
}

function clearError(control) {
  control.removeAttribute('aria-invalid');
  const msg = messageFor(control);
  if (msg) {
    msg.textContent = '';
    msg.hidden = true;
  }
}

/** Validate one control against its data-rod-* rules. Returns true when valid. */
function validateControl(control) {
  const required = control.hasAttribute('data-rod-required');
  const kind = control.getAttribute('data-rod-type') || '';
  const requiredMsg = control.getAttribute('data-rod-msg-required') || '';
  const formatMsg = control.getAttribute('data-rod-msg-format') || '';
  const isCheckbox = control.type === 'checkbox';
  const value = isCheckbox ? '' : String(control.value || '').trim();

  if (required && isCheckbox && !control.checked) {
    showError(control, requiredMsg);
    return false;
  }
  if (required && !isCheckbox && !value) {
    showError(control, requiredMsg);
    return false;
  }
  if (value && kind === 'email' && !EMAIL_RE.test(value)) {
    showError(control, formatMsg);
    return false;
  }
  if (value && kind === 'tel') {
    const d = digitsOf(value);
    // Letters and other stray characters are rejected before the digits are counted, so that
    // "05305326451abc" cannot pass as a phone number (spaces, +, (), - and / stay allowed).
    if (!TEL_RE.test(value) || d.length < 10 || d.length > 13) {
      showError(control, formatMsg);
      return false;
    }
  }
  clearError(control);
  return true;
}

function lockSubmit(form) {
  const button = form.querySelector('[data-rod-submit]');
  if (!button) return;
  button.setAttribute('aria-disabled', 'true');
  button.classList.add('is-loading');
  try {
    if (Rod && typeof Rod.icon === 'function') {
      const spinner = Rod.icon('loader-circle');
      if (spinner && spinner.childNodes.length) button.prepend(spinner);
    }
  } catch (e) {
    /* the aria-disabled state is the real signal */
  }
}

function unlockSubmit(form) {
  const button = form.querySelector('[data-rod-submit]');
  if (!button) return;
  button.removeAttribute('aria-disabled');
  button.classList.remove('is-loading');
  const spinner = button.querySelector('.rod-icon--loader-circle');
  if (spinner) spinner.remove();
}

/** Turkish retry message in the form's own banner slot (never an English browser string). */
function showFormBanner(form, text) {
  let banner = form.querySelector('[data-rod-form-retry]');
  if (!banner) {
    banner = document.createElement('p');
    banner.className = 'rod-form__banner rod-form__banner--error';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('tabindex', '-1');
    banner.setAttribute('data-rod-form-retry', '');
    form.prepend(banner);
  }
  banner.textContent = text;
  banner.hidden = false;
}

function initValidatedForm(form) {
  const controls = Array.from(form.querySelectorAll('[data-rod-validate]'));
  if (!controls.length) return;
  // Replace the browser's English bubbles with our Turkish inline messages; `required` and the field
  // names stay in the markup so the form still enforces itself without JavaScript (V-05).
  form.noValidate = true;
  let submitting = false;
  let watchdog = 0;

  controls.forEach((control) => {
    const revalidate = () => {
      if (control.getAttribute('aria-invalid') === 'true') validateControl(control);
    };
    control.addEventListener('input', revalidate);
    control.addEventListener('change', revalidate);
    control.addEventListener('blur', () => {
      if (control.getAttribute('aria-invalid') === 'true' || String(control.value || '').trim()) validateControl(control);
    });
  });

  form.addEventListener('submit', (event) => {
    if (submitting) {
      event.preventDefault();
      return;
    }
    const invalid = controls.filter((control) => !validateControl(control));
    if (invalid.length) {
      event.preventDefault();
      invalid[0].focus();
      return;
    }
    submitting = true;
    lockSubmit(form);
    /* Shopify's captcha script intercepts the submit and re-submits once it has a token. If that
       round trip never completes the visitor was left with a button that spun forever and every
       further click swallowed by the `submitting` guard. After 15 s the form becomes usable again
       and says so in Turkish. */
    window.clearTimeout(watchdog);
    watchdog = window.setTimeout(() => {
      if (!submitting) return;
      submitting = false;
      unlockSubmit(form);
      showFormBanner(form, 'Mesajınız gönderilemedi. Lütfen tekrar deneyin.');
    }, 15000);
  });

  /* A bfcache restore (Back from the success page) would otherwise bring the locked button back. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    window.clearTimeout(watchdog);
    submitting = false;
    unlockSubmit(form);
  });
}

/* ------------------------------------------------------------------ 4. contact prefill */

function initContactPrefill(form) {
  let params;
  try {
    params = new URLSearchParams(location.search);
  } catch (e) {
    return;
  }
  const konu = (params.get('konu') || '').trim();
  const urun = (params.get('urun') || params.get('q') || '').trim();

  if (konu) {
    const select = form.querySelector('[data-rod-konu-select]');
    const option = select && select.querySelector(`option[data-rod-konu="${CSS.escape(konu)}"]`);
    if (select && option) select.value = option.value;
  }

  if (urun) {
    const body = form.querySelector('[data-rod-urun-target]');
    const template = body && body.getAttribute('data-rod-urun-template');
    if (body && template && !String(body.value || '').trim()) {
      body.value = `${template.replace('{urun}', urun)}\n\n`;
    }
  }
}

/* ------------------------------------------------------------------ 5. post-submit banner */

/** Move focus to the success/error banner Shopify's POST redirect brought back.
    A role="status"/role="alert" element that is already in the initial HTML is never announced
    (live regions only announce later mutations), so without this a screen-reader user gets no
    feedback at all; the markup already carries `tabindex="-1"` for exactly this. */
function focusBanner() {
  const banner = document.querySelector('[data-rod-page-root] .rod-form__banner');
  if (!banner) return;
  try {
    banner.focus();
  } catch (e) {
    /* focus is progressive enhancement */
  }
}

/* ------------------------------------------------------------------ 6. password owner dialog */

/** Close the owner dialog when the dim area around the panel is clicked.
    Horizon's own outside-click test compares the click with the <dialog> box, and Horizon's password
    dialog fills the viewport, so no click is ever "outside" it. Our restyle draws that box as a dim
    backdrop with a centred panel, so the dim area has to behave like a backdrop. */
function initPasswordDialogClose(panel) {
  const host = panel.closest('dialog-component');
  const dialog = panel.closest('dialog');
  if (!host || !dialog) return;
  dialog.addEventListener('click', (event) => {
    if (!dialog.open) return;
    // Keyboard-activated clicks (Enter/Space on a button) carry no pointer position.
    if (!event.detail) return;
    if (event.target instanceof Node && panel.contains(event.target)) return;
    const r = panel.getBoundingClientRect();
    if (event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom) return;
    if (typeof host.closeDialog === 'function') host.closeDialog();
    else dialog.close();
  });
}

/** Open the storefront-password dialog as a REAL modal after a wrong password.
    `layout/password.liquid` only marks the error state: a dialog carrying the bare `open` attribute
    is non-modal, and Horizon's `dialog-component.showDialog()` returns early on `dialog.open`, so
    showModal(), the focus trap, Esc and the outside-click handler would never be wired. */
function initPasswordDialog() {
  const host = document.querySelector('dialog-component[data-rod-password-error]');
  if (!host) return;
  const dialog = host.querySelector('dialog');
  if (!dialog) return;

  const openNative = () => {
    if (dialog.open) return;
    try {
      dialog.showModal();
    } catch (e) {
      dialog.setAttribute('open', '');
    }
  };

  const open = () => {
    if (dialog.open) return;
    if (typeof host.showDialog !== 'function') {
      openNative();
      return;
    }
    try {
      host.showDialog();
    } catch (e) {
      openNative();
      return;
    }
    // dialog-component defers showModal() to the next animation frame; if it never lands (script
    // error elsewhere) the panel still has to reach the screen, so the wrong-password message is seen.
    setTimeout(openNative, 500);
  };

  if (window.customElements && typeof customElements.whenDefined === 'function') {
    let defined = false;
    customElements.whenDefined('dialog-component').then(
      () => {
        defined = true;
        open();
      },
      () => {
        /* handled by the timer below */
      }
    );
    setTimeout(() => {
      if (!defined) open();
    }, 1200);
  } else {
    open();
  }
}

/* ------------------------------------------------------------------ yardım merkezi menüsü */

/* Yardım Merkezi menüsü (sections/rod-help-page.liquid) telefonda kapalı bir açılır kutudur;
   masaüstünde solda sabit durur ve hep açıktır. Kutu <details> olduğu için betik yalnız masaüstünde
   açar; ekran daralınca ziyaretçinin açık bıraktığı kutuya dokunulmaz. */
function initHelpMenu(root) {
  const menu = root.querySelector('[data-rod-hc-menu]');
  if (!menu) return;
  const wide = window.matchMedia('(min-width: 990px)');
  const sync = () => {
    if (wide.matches) menu.open = true;
  };
  sync();
  wide.addEventListener('change', sync);
}

/* ------------------------------------------------------------------ boot */

function init() {
  const roots = Array.from(document.querySelectorAll('[data-rod-page-root]'));
  roots.forEach((root) => {
    initToc(root);
    initHelp(root);
    initHelpMenu(root);
    // Rod forms are recognised by the validation hooks on their own controls, so a form needs no extra
    // marker attribute here (the password sign-up also carries the 3.4 `data-rod-newsletter` hook).
    // Forms outside [data-rod-page-root] (the WP-8 footer newsletter) are never touched.
    root.querySelectorAll('form').forEach((form) => {
      if (!form.querySelector('[data-rod-validate]')) return;
      initValidatedForm(form);
      if (form.querySelector('[data-rod-konu-select], [data-rod-urun-target]')) initContactPrefill(form);
    });
  });
  const passwordPanel = document.querySelector('.rod-pwd__panel');
  if (passwordPanel) initPasswordDialogClose(passwordPanel);
  // The wrong-password modal owns the focus when it is there; otherwise the returned banner does.
  const passwordError = document.querySelector('dialog-component[data-rod-password-error]');
  if (passwordError) initPasswordDialog();
  else focusBanner();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
